import {IconType} from "react-icons";
import {FaCode} from "react-icons/fa6";
import {FiEdit2, FiShield} from "react-icons/fi";
import {TbFileCertificate} from "react-icons/tb";

import {AccountUser} from "@/lib/types/accounts.ts";
import {Service} from "@/lib/types/checks.ts";

import {GraphQLResponse} from "./common";

export enum CredentialType {
  BASIC = "BASIC",
  CERTIFICATE = "CERTIFICATE",
  TOKEN = "TOKEN",
  TOTP = "TOTP",
}

export const CredentialTypeLabel: Record<CredentialType, string> = {
  [CredentialType.BASIC]: "Username and Password",
  [CredentialType.CERTIFICATE]: "Certificate",
  [CredentialType.TOKEN]: "Single Secret Token",
  [CredentialType.TOTP]: "Time-based One-time Password",
};

export interface CredentialTypeDetails {
  label: string;
  description: string;
  icon: IconType;
}

export const CredentialTypeDisplayDetails: Record<CredentialType, CredentialTypeDetails> = {
  [CredentialType.BASIC]: {
    label: CredentialTypeLabel[CredentialType.BASIC],
    description: "A username and password pair often used for HTTP basic authentication to use in your Checks",
    icon: FiEdit2,
  },
  [CredentialType.TOKEN]: {
    label: CredentialTypeLabel[CredentialType.TOKEN],
    description: "A single secret token that can be used in your checks",
    icon: FiShield,
  },
  [CredentialType.CERTIFICATE]: {
    label: CredentialTypeLabel[CredentialType.CERTIFICATE],
    description: "A certificate used to authenticate with your checks",
    icon: TbFileCertificate,
  },
  [CredentialType.TOTP]: {
    label: CredentialTypeLabel[CredentialType.TOTP],
    description: "A Time-based One-Time Password used to authenticate with your checks",
    icon: FaCode,
  },
};

export const CredentialPropertyLabels: Record<string, string> = {
  password: "Password",
  username: "Username",
  certificate: "Certificate",
  key: "Key File",
  passphrase: "Passphrase",
  secret: "Secret",
  period: "Period",
  num_digits: "Number of Digits",
};

export interface CredentialSecret {
  password?: string;
  certificate?: string;
  certificate_file?: FileList;
  key_file?: FileList;
  key?: string;
  passphrase?: string;
  secret?: string;
  period?: number;
  num_digits?: number;
}

export interface Credentials {
  id: string;
  account: string;
  credential_type: CredentialType;
  display_name: string;
  description?: string;
  hint?: string;
  username?: string;
  version: number;
  used_secret_properties: string[];
  created_at: string;
  created_by?: AccountUser;
  service_variable_count?: number;
  services: Service[];
}

export type CredentialResponse = GraphQLResponse<Credentials>;

export type CredentialListResponse = GraphQLResponse<Credentials[]>;

export type CredentialCreateResponse = GraphQLResponse<Credentials>;

export type CredentialUpdateResponse = GraphQLResponse<Credentials>;

export type CredentialDeleteResponse = GraphQLResponse<Credentials>;
